import { cn } from "@newxsystems/shared";
import { resolveKaajPrimitive } from "../../../../client";
import { RichTextHtml } from "../../../../cms/components/RichTextHtml";
import type {
  HeroBlockAction,
  HeroBlockImage,
} from "../../../../cms/components/blocks/HeroBlock/HeroBlock.props";
import type { IHeroCenteredProps } from "./HeroCentered.props";

const Image = resolveKaajPrimitive("Image");
const Link = resolveKaajPrimitive("Link");

const isExternalBlank = (target?: string) => target === "_blank";

const HeroActions = ({
  buttons,
  centered = false,
}: {
  buttons?: HeroBlockAction[] | null;
  centered?: boolean;
}) => {
  const visibleActions = buttons ?? [];

  if (visibleActions.length === 0) {
    return null;
  }

  return (
    <div
      className={cn("flex flex-wrap items-center gap-3", centered ? "justify-center" : undefined)}
    >
      {visibleActions.map((action, index) => {
        const label = action.text;
        const style = action.style;
        const isPrimary = style === "primary" || (index === 0 && style !== "link");

        return (
          <Link
            key={`${label}-${action.href}`}
            href={action.href}
            target={action.target}
            rel={isExternalBlank(action.target) ? "noopener noreferrer" : undefined}
            aria-label={action.ariaLabel || label}
            className={cn(
              "inline-flex min-h-11 items-center justify-center rounded-full px-5 py-3 text-sm font-bold transition-[background-color,border-color,box-shadow,transform] duration-200 motion-reduce:transition-none max-[420px]:w-full",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2144FF]",
              isPrimary
                ? "bg-[#2144FF] text-white shadow-[0_14px_34px_rgba(33,68,255,0.25)] hover:bg-[#132EDB] active:bg-[#0F25B8] motion-safe:hover:-translate-y-0.5"
                : "border border-[#D9E1FF] bg-white/85 text-[#2144FF] hover:border-[#2144FF] hover:bg-[#F6F1FF] hover:shadow-[0_12px_30px_rgba(33,68,255,0.12)] active:border-[#132EDB] active:text-[#132EDB]",
            )}
          >
            {label}
            {isPrimary ? (
              <span aria-hidden="true" className="ml-2">
                →
              </span>
            ) : null}
          </Link>
        );
      })}
    </div>
  );
};

const ProductPanel = ({ image, priority }: { image?: HeroBlockImage; priority?: boolean }) => {
  const src = image?.defaultSrc;
  const altText = image?.alternateText ?? "";

  if (!src) {
    return null;
  }

  return (
    <figure className="relative overflow-hidden rounded-[2rem] border border-[#D9E1FF] bg-[#FAFBFF] p-3 shadow-[0_28px_80px_rgba(33,68,255,0.14)]">
      <div className="rounded-[1.5rem] border border-[#D9E1FF] bg-white">
        <Image
          src={src}
          alt={altText}
          width={image?.width ?? undefined}
          height={image?.height ?? undefined}
          className="aspect-[16/10] w-full rounded-[1.5rem] object-cover"
          loading={priority ? "eager" : "lazy"}
          priority={priority}
          sizes={image.sizes ?? "(min-width: 1024px) 64rem, 100vw"}
          srcSet={image.srcSet ?? undefined}
          style={
            image.focalPoint
              ? { objectPosition: `${image.focalPoint.x}% ${image.focalPoint.y}%` }
              : undefined
          }
        />
      </div>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full border border-[#8FA3FF]/50"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-12 left-8 h-24 w-24 rounded-full bg-[#FFEAF9]/80 blur-2xl"
      />
      {image.caption ? (
        <figcaption className="px-3 pt-3 text-sm leading-6 text-[#4B5568]">
          {image.caption}
        </figcaption>
      ) : null}
    </figure>
  );
};

const HeroCopy = ({
  props,
  centered = false,
}: {
  props: IHeroCenteredProps;
  centered?: boolean;
}) => {
  return (
    <div
      className={cn(
        "flex flex-col gap-5 sm:gap-6",
        centered
          ? "mx-auto max-w-4xl items-center text-center"
          : "max-w-[44rem] items-start text-left",
      )}
    >
      {props.eyebrow || props.heading ? (
        <div>
          {props.eyebrow ? (
            <p className="mb-4 text-xs font-extrabold uppercase tracking-[0.18em] text-[#2144FF]">
              {props.eyebrow}
            </p>
          ) : null}
          {props.heading ? (
            <h1
              className={cn(
                "text-balance text-[3.45rem] lg:text-[5rem] font-extrabold leading-[1.04] tracking-[-0.04em]",
                "text-[#151927]",
              )}
            >
              {props.heading}
            </h1>
          ) : null}
        </div>
      ) : null}
      {props.subheading ? (
        <RichTextHtml
          className={cn(
            "text-pretty text-base leading-7 sm:text-lg sm:leading-8",
            "text-[#4B5568]",
            centered ? "max-w-[640px]" : "max-w-[620px]",
          )}
          html={props.subheading}
        />
      ) : null}
      <div className={cn(centered ? "flex justify-center" : undefined, "max-[420px]:w-full")}>
        <HeroActions buttons={props.buttons} centered={centered} />
      </div>
    </div>
  );
};

export const HeroCentered = (props: IHeroCenteredProps) => {
  const panelImage = props.image ?? undefined;
  const hasPanelImage = Boolean(panelImage?.defaultSrc);
  const isLeftAligned = props.alignment === "left";
  const isExplicitCentered = props.alignment === "center";

  if (isLeftAligned) {
    if (!hasPanelImage) {
      return (
        <div className="flex max-w-[52rem] flex-col justify-center py-6 sm:py-8 lg:min-h-[30rem] lg:py-10">
          <HeroCopy props={props} />
        </div>
      );
    }

    return (
      <div className="grid items-center gap-10 lg:grid-cols-[minmax(0,0.85fr)_minmax(28rem,1fr)] lg:gap-16">
        <HeroCopy props={props} />
        <ProductPanel image={panelImage} priority={props.priority ?? undefined} />
      </div>
    );
  }

  if (!hasPanelImage) {
    return (
      <div
        className={cn(
          "mx-auto flex flex-col justify-center",
          isExplicitCentered
            ? "max-w-[56rem] py-4 sm:py-8 lg:py-10"
            : "max-w-4xl py-6 sm:py-8 lg:py-10",
        )}
      >
        <HeroCopy props={props} centered />
      </div>
    );
  }

  return (
    <div className="space-y-12 lg:space-y-14">
      <HeroCopy props={props} centered />
      <div className="mx-auto max-w-5xl">
        <ProductPanel image={panelImage} priority={props.priority ?? undefined} />
      </div>
    </div>
  );
};

HeroCentered.displayName = "content::HeroCentered";
